import type { BudgetResponse } from "../api/types";
import { SERVICE_LABELS, aud, sydneyStamp, tilesLabel } from "./format";

function stamp(iso: string | null): string {
  return iso === null ? "not yet" : sydneyStamp(iso);
}

/** Spec §9/§13: the admin-only budget card — spend, per-service breakdown,
 *  warning levels fired this month, the FX rate used, and the pause/health
 *  state a paused site or an unhealthy poller need to surface loudly. */
export function BudgetCard({
  budget,
  error,
}: {
  budget: BudgetResponse | null;
  error: string | null;
}): JSX.Element {
  const box = {
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "12px 16px",
    marginBottom: 24,
  };
  if (error !== null || budget === null) {
    return (
      <section style={box}>
        <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Budget</h2>
        <p style={{ margin: 0 }}>
          {error === null ? "Loading…" : `Couldn't load the budget: ${error}`}
        </p>
      </section>
    );
  }
  const rec = budget.record;
  return (
    <section style={box}>
      <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Budget</h2>
      {budget.pause === null ? (
        <p style={{ margin: "0 0 8px", color: "#166534" }}>Serving</p>
      ) : (
        <p role="alert" style={{ margin: "0 0 8px", color: "#b91c1c" }}>
          Paused since {stamp(budget.pause.at)} by {budget.pause.by} ({budget.pause.reason}):{" "}
          {budget.pause.detail}
        </p>
      )}
      {rec === null ? (
        <p style={{ margin: 0 }}>
          {budget.monthError === null
            ? "No month record yet (the budget Worker hasn't run this month)."
            : `Month record unreadable: ${budget.monthError}`}
        </p>
      ) : (
        <>
          <p style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700 }}>
            {aud(rec.spendAud)}{" "}
            <span style={{ fontSize: 13, fontWeight: 400, color: "#666" }}>
              so far this month (last poll {stamp(rec.updatedAt)})
            </span>
          </p>
          {rec.health.consecutiveFailures > 0 && (
            <p style={{ margin: "0 0 8px", color: "#b45309" }}>
              Poller unhealthy:{" "}
              {rec.health.lastError ?? `${rec.health.consecutiveFailures} failed polls`}
            </p>
          )}
          <table style={{ borderCollapse: "collapse", fontSize: 14, marginBottom: 8 }}>
            <tbody>
              {Object.entries(rec.byService).map(([key, row]) => (
                <tr key={key}>
                  <td style={{ padding: "2px 12px 2px 0" }}>{SERVICE_LABELS[key] ?? key}</td>
                  <td style={{ padding: "2px 12px 2px 0", textAlign: "right" }}>
                    {tilesLabel(row.units)}
                  </td>
                  <td style={{ padding: "2px 0", textAlign: "right" }}>{aud(row.aud)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ margin: "0 0 4px", fontSize: 13, color: "#444" }}>
            Warnings:{" "}
            {["25", "50", "75", "100"]
              .map((l) => `${l}% ${stamp(rec.fired[l] ?? null)}`)
              .join(" · ")}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "#444" }}>
            USD→AUD {rec.fx.usdAud} ({rec.fx.source})
          </p>
        </>
      )}
    </section>
  );
}
